/**
 * SerenLoop — Nausea Type Quiz
 * Self-contained quiz logic with Klaviyo integration.
 *
 * The .sl-quiz wrapper element must provide:
 *   data-sl-company-id  — Klaviyo public API key
 *   data-sl-list-id     — Klaviyo list ID
 */

(function () {
  'use strict';

  // ─── STATE ───
  var answers = {};
  var currentScreen = 0;

  // ─── NAUSEA TYPE NAMES ───
  var nauseaTypeNames = {
    morning:   'The Morning Wave',
    postmeal:  'The Post-Meal Surge',
    injection: 'The Dose-Day Crash',
    random:    'The Random Hit',
    constant:  'The All-Day Simmer'
  };

  // ─── MEDICATION DISPLAY NAMES ───
  var medicationDisplayNames = {
    semaglutide: 'Semaglutide (Ozempic / Wegovy)',
    tirzepatide: 'Tirzepatide (Mounjaro / Zepbound)',
    other:       'Other GLP-1',
    unsure:      'GLP-1 (unsure which)'
  };

  // ─── NAUSEA TYPE EXPLANATIONS ───
  var nauseaTypeExplanations = {
    morning: {
      when: 'Before noon, especially before eating.',
      why:  'Your medication works around the clock \u2014 even while you sleep. With nothing in your stomach, there\u2019s nothing to distract your body from the nausea signal. Your stomach also slows down overnight, so acid and bile sit longer than they should.',
      research: 'A Cochrane review of 77 studies (9,847 people) found that applying pressure to the P6 wrist point before nausea starts reduced it by 28\u201375%. (Lee et al., Cochrane Database, 2025)',
      helps: [
        'Small snack before bed or on waking',
        'P6 acupressure while still in bed',
        'Ginger 250mg \u2014 blocks the same nausea pathway as Zofran, just more gently'
      ]
    },
    postmeal: {
      when: '30\u201360 minutes after eating.',
      why:  'Your medication slows your stomach down \u2014 a lot. Food sits there longer than your body expects, and your stomach stretches more than it normally would. Fatty or heavy meals make it worse. This effect is strongest in the first 4\u20135 weeks.',
      research: 'A multidisciplinary expert panel confirmed that smaller, more frequent meals are the most effective dietary change for GLP-1 nausea. (Trujillo et al., Advances in Therapy, 2022)',
      helps: [
        '5\u20136 smaller meals instead of 3 big ones',
        'Low-fat moderate-protein foods',
        'P6 acupressure 5\u201310 minutes before eating',
        'Stay upright 30 minutes after eating'
      ]
    },
    injection: {
      when: 'Worst 24\u201348 hours after injection, then fades.',
      why:  'Your medication peaks in your blood about 24 hours after injection. As the concentration rises, it hits the nausea center in your brainstem hard. It\u2019s worse with each dose increase because your brain hasn\u2019t had time to adjust to the higher level yet.',
      research: 'Semaglutide reaches peak concentration at ~24 hours post-injection with a 7-day half-life. (Overgaard et al., Drug Design Development & Therapy, 2024)',
      helps: [
        'Start P6 acupressure the morning of injection day',
        'Ginger 250mg for 2\u20133 days',
        'Vitamin B6 25mg up to 3x/day',
        'Inject before bed to sleep through the initial peak'
      ]
    },
    random: {
      when: 'No clear pattern. Comes and goes unpredictably.',
      why:  'Your medication stays in your system all week, so your brain\u2019s nausea center is always \u201con\u201d at a low level. Hormonal shifts, stress, hydration can tip you over the threshold at different times. This is the hardest type to manage reactively.',
      research: 'P6 acupressure significantly increases vagal nerve activity, which helps regulate your body\u2019s nausea response throughout the day. (Frey et al., Clinical Autonomic Research, 2005)',
      helps: [
        'Wearing a P6 band throughout the day',
        'Keeping ginger lozenges nearby',
        'Staying hydrated \u2014 8+ glasses a day',
        'Slow deep breathing when a wave hits'
      ]
    },
    constant: {
      when: 'Low-grade, constant, never fully gone.',
      why:  'Your body hasn\u2019t fully adjusted to the medication yet. Your stomach may not be emptying completely between meals, and the nausea center in your brain is getting a constant signal. This is usually dose-related.',
      research: 'Expert guidelines recommend dose reduction if nausea persists beyond one month at stable dose. Acupressure combined with anti-nausea medication reduced symptoms significantly more than medication alone. (Maselli & Camilleri, Postgraduate Medicine, 2021)',
      helps: [
        'Layer multiple approaches \u2014 P6 + ginger + smaller meals',
        'B6 supplementation 25mg 2\u20133x daily',
        'Talk to prescriber about dose pacing',
        'Consistent daily routine matters more than intensity'
      ]
    }
  };

  // ─── NAVIGATION ───
  function goTo(n) {
    var current = document.getElementById('screen-' + currentScreen);
    var next = document.getElementById('screen-' + n);

    if (current) {
      current.classList.remove('visible');
      setTimeout(function () { current.classList.remove('active'); }, 350);
    }

    setTimeout(function () {
      next.classList.add('active');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          next.classList.add('visible');
        });
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, n > currentScreen ? 380 : 100);

    currentScreen = n;

    if (n === 7) buildResults();
  }

  // ─── OPTION SELECTION ───
  function initOptionSelection() {
    document.querySelectorAll('.sl-quiz .options').forEach(function (group) {
      var type = group.dataset.type;
      var question = group.dataset.question;

      group.querySelectorAll('.option').forEach(function (opt) {
        opt.addEventListener('click', function () {
          if (type === 'single') {
            group.querySelectorAll('.option').forEach(function (o) { o.classList.remove('selected'); });
            opt.classList.toggle('selected');
            answers[question] = opt.dataset.value;
          } else {
            opt.classList.toggle('selected');
            answers[question] = Array.from(group.querySelectorAll('.option.selected')).map(function (o) { return o.dataset.value; });
          }

          // Enable next button
          var screenNum = opt.closest('.screen').id.split('-')[1];
          var btn = document.getElementById('next-' + screenNum);
          if (btn) {
            var hasSelection = type === 'single'
              ? group.querySelector('.option.selected')
              : group.querySelectorAll('.option.selected').length > 0;
            btn.disabled = !hasSelection;
          }
        });
      });
    });
  }

  // ─── EMAIL SUBMIT ───
  function submitEmail() {
    var nameInput = document.getElementById('input-name');
    var emailInput = document.getElementById('input-email');
    var name = nameInput ? nameInput.value.trim() : '';
    var email = emailInput ? emailInput.value.trim() : '';

    if (!email || email.indexOf('@') === -1 || email.indexOf('.') === -1) {
      if (emailInput) {
        emailInput.style.borderColor = '#B88B88';
        emailInput.focus();
        emailInput.addEventListener('input', function clearError() {
          emailInput.style.borderColor = '';
          emailInput.removeEventListener('input', clearError);
        }, { once: true });
      }
      return;
    }

    answers.name = name;
    answers.email = email;

    // Prevent double submission
    var submitBtn = document.querySelector('.sl-quiz .sl-email-submit');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.style.opacity = '0.6';
    }

    // ─── KLAVIYO INTEGRATION ───
    var quizWrap = document.querySelector('.sl-quiz');
    var companyId = quizWrap ? quizWrap.getAttribute('data-sl-company-id') : '';
    var listId = quizWrap ? quizWrap.getAttribute('data-sl-list-id') : '';

    var triedArr = answers.tried || [];
    var quizProperties = {
      quiz_completed: true,
      quiz_completed_date: new Date().toISOString(),
      medication: answers.medication,
      medication_display: medicationDisplayNames[answers.medication] || 'GLP-1',
      dose_stage: answers.stage,
      nausea_pattern: answers.pattern,
      nausea_type_name: nauseaTypeNames[answers.pattern] || '',
      tried_ginger: triedArr.includes('ginger'),
      tried_small_meals: triedArr.includes('small-meals'),
      tried_medication: triedArr.includes('medication'),
      tried_bands: triedArr.includes('bands'),
      tried_pushing_through: triedArr.includes('pushing-through'),
      tried_nothing: triedArr.includes('nothing'),
      severity: answers.severity,
      injection_day: answers.injection_day,
      source: 'nausea_quiz',
      prelaunch: true
    };

    if (!companyId || !listId) {
      console.warn('[SerenLoop] Klaviyo company_id or list_id not configured on .sl-quiz. Showing success state anyway.');
      showQuizSuccess();
      return;
    }

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'SerenLoop Nausea Quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: name,
                properties: quizProperties
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId
            }
          }
        }
      }
    };

    var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId);

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (response.ok || response.status === 202) {
          showQuizSuccess();
        } else {
          console.warn('[SerenLoop] Klaviyo returned status:', response.status);
          showQuizSuccess();
        }
      })
      .catch(function (err) {
        console.error('[SerenLoop] Klaviyo submission error:', err);
        showQuizSuccess();
      });
  }

  // ─── SHOW SUCCESS STATE ───
  function showQuizSuccess() {
    // Swap opt-in form for confirmation
    var optinEl = document.getElementById('email-optin');
    var confirmedEl = document.getElementById('email-confirmed');
    if (optinEl) optinEl.style.display = 'none';
    if (confirmedEl) confirmedEl.style.display = 'block';

    var dayNames = {
      monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
      thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday',
      sunday: 'Sunday', daily: 'every'
    };
    var confirmDay = document.getElementById('confirm-day');
    if (confirmDay) confirmDay.textContent = dayNames[answers.injection_day] || '';

    // Google Ads conversion tracking
    try {
      if (typeof gtag === 'function') {
        gtag('event', 'conversion', {
          'send_to': 'AW-18008211026/FmXGCOOWzoYcENL8_YpD',
          'event_callback': function () { /* conversion recorded */ }
        });
      }
    } catch (e) {
      /* gtag unavailable */
    }

    // Track in sessionStorage
    try {
      sessionStorage.setItem('sl_email_submitted', 'true');
      sessionStorage.setItem('sl_email_source', 'nausea_quiz');
      sessionStorage.setItem('sl_email_time', Date.now().toString());
    } catch (e) {
      /* sessionStorage unavailable */
    }

    // Smooth scroll to confirmation
    if (confirmedEl) {
      confirmedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // ─── RESULTS BUILDER ───
  function buildResults() {
    var medication = answers.medication;
    var stage = answers.stage;
    var pattern = answers.pattern;
    var tried = answers.tried;
    var severity = answers.severity;
    var injection_day = answers.injection_day;
    var name = answers.name;

    var medName = ({
      semaglutide: 'semaglutide',
      tirzepatide: 'tirzepatide',
      other: 'your GLP-1',
      unsure: 'your GLP-1'
    })[medication] || 'your GLP-1';

    var dayName = ({
      monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
      thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday',
      sunday: 'Sunday', daily: 'daily'
    })[injection_day] || '';

    var isDaily = injection_day === 'daily';

    // Personalise title with nausea type name
    var typeName = nauseaTypeNames[pattern] || 'Your Nausea Type';
    var titleName = name ? ', ' + name : '';
    var resultsTitle = document.getElementById('results-title');
    if (resultsTitle) {
      resultsTitle.textContent = 'You\u2019re a ' + typeName + titleName;
    }

    // Populate injection day in opt-in section
    var dayNamesMap = {
      monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
      thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday',
      sunday: 'Sunday', daily: 'every'
    };
    var optinDay = document.getElementById('optin-day');
    if (optinDay) optinDay.textContent = dayNamesMap[injection_day] || '';

    // Subtitle based on stage — warm, encouraging tone
    var subtitles = {
      starting:  'The first weeks on ' + medName + ' are often the roughest for nausea. Here\u2019s a relief routine built specifically for this window.',
      titrating: 'Dose increases can reset the nausea clock. Your body is recalibrating \u2014 this routine accounts for that.',
      stable:    'Persistent nausea on a stable dose responds well to consistent P6 pressure. Timing and regularity are your best friends here.',
      veteran:   'After six months, your nausea patterns are well-established. This routine targets the specific windows you described.'
    };
    var resultsSubtitle = document.getElementById('results-subtitle');
    if (resultsSubtitle) resultsSubtitle.textContent = subtitles[stage] || '';

    var html = '';

    // ─── NAUSEA TYPE EXPLANATION ───
    var typeInfo = nauseaTypeExplanations[pattern];
    if (typeInfo) {
      html += '<div class="protocol-section type-explanation">';
      html += '<div class="protocol-label">Your nausea type</div>';
      html += '<h3>' + typeName + '</h3>';
      html += '<div class="type-detail">';
      html += '<p><strong>When it happens:</strong> ' + typeInfo.when + '</p>';
      html += '<p><strong>Why it happens:</strong> ' + typeInfo.why + '</p>';
      html += '<p class="type-research"><em>' + typeInfo.research + '</em></p>';
      html += '<p><strong>What helps:</strong></p>';
      html += '<ul class="type-helps">';
      typeInfo.helps.forEach(function (item) {
        html += '<li>' + item + '</li>';
      });
      html += '</ul>';
      html += '</div>';
      html += '</div>';
    }

    // ─── SECTION 1: YOUR DAILY RELIEF ROUTINE ───
    html += '<div class="protocol-section">';
    html += '<div class="protocol-label">Your daily relief routine</div>';
    html += '<h3>When to Apply P6 Pressure</h3>';
    html += '<p>Based on your nausea pattern \u2014 ' + getPatternDescription(pattern) + ' \u2014 here\u2019s your daily timing.</p>';

    html += buildTimingProtocol(pattern, severity, stage);
    html += '</div>';

    // ─── SECTION 2: INJECTION DAY RELIEF PLAN ───
    if (!isDaily) {
      html += '<div class="protocol-section">';
      html += '<div class="protocol-label">Your ' + dayName + ' relief plan</div>';
      html += '<h3>Injection Day + Recovery Window</h3>';

      if (medication === 'tirzepatide') {
        html += '<p>Tirzepatide activates both GIP and GLP-1 receptors, which means nausea can be more intense but also more predictable \u2014 typically peaking 12\u201336 hours post-injection. Your ' + dayName + ' relief plan is more intensive.</p>';
      } else {
        html += '<p>' + medName.charAt(0).toUpperCase() + medName.slice(1) + ' nausea typically peaks 24\u201348 hours after injection. Your ' + dayName + ' relief plan front-loads support during that window.</p>';
      }

      html += buildInjectionProtocol(dayName, medication, severity);
      html += '</div>';
    } else {
      html += '<div class="protocol-section">';
      html += '<div class="protocol-label">Daily medication relief plan</div>';
      html += '<h3>Morning Dosing Window</h3>';
      html += '<p>With daily oral ' + medName + ', nausea typically peaks 30\u201390 minutes after your dose. Apply P6 pressure for 3 minutes per wrist starting 10 minutes before you take your tablet, and keep pressure steady through the absorption window.</p>';
      html += '</div>';
    }

    // ─── SECTION 3: DOSE INCREASE PREP ───
    if (stage === 'starting' || stage === 'titrating') {
      html += '<div class="protocol-section">';
      html += '<div class="protocol-label">Preparing for your next step</div>';
      html += '<h3>Getting Ready for Your Next Increase</h3>';

      if (stage === 'starting') {
        html += '<p>Your first dose increase is coming \u2014 and it\u2019s the transition most women find hardest. Start doing the full morning routine every day for the week before your increase, whether you feel nauseous or not. You\u2019re building the habit now so it\u2019s automatic when you need it most.</p>';
      } else {
        html += '<p>You\u2019re in the recalibration window. Your body typically adjusts within 7\u201314 days at the new dose. During this period, treat every day like an injection day: morning routine, pre-meal routine, and a session before bed. The consistency during this window is what separates women who push through the titration from those who step back down.</p>';
      }
      html += '</div>';
    }

    // ─── SECTION 4: WHAT YOU'VE TRIED — CONTEXTUAL ADVICE ───
    var triedArr = tried || [];
    if (triedArr.length > 0 && !triedArr.includes('nothing')) {
      html += '<div class="protocol-section">';
      html += '<div class="protocol-label">Building on what you know</div>';
      html += '<h3>Working With What You\u2019ve Already Tried</h3>';

      if (triedArr.includes('ginger')) {
        html += '<div class="callout"><div class="callout-label">On ginger</div><p>Ginger works on a different mechanism than P6 \u2014 it\u2019s a 5-HT3 antagonist, while acupressure modulates vagal signalling. They complement each other. If ginger helps but doesn\u2019t fully resolve your nausea, adding P6 pressure addresses the pathway ginger doesn\u2019t reach. Standardized ginger extract (250mg, \u22655% gingerols) is more effective than ginger tea or candy.</p></div>';
      }

      if (triedArr.includes('bands')) {
        html += '<div class="callout"><div class="callout-label">On acupressure bands</div><p>If you\u2019ve tried a band with a plastic bead and found it left marks, caused numbness, or required constant repositioning \u2014 the technique was right but the delivery was wrong. Plastic beads concentrate pressure at 15\u201325mm\u00B2, causing median nerve compression. The clinical research supports broad, distributed pressure across 80\u2013120mm\u00B2 \u2014 the contact area of a practitioner\u2019s thumb.</p></div>';
      }

      if (triedArr.includes('medication')) {
        html += '<div class="callout"><div class="callout-label">On anti-nausea medication</div><p>P6 acupressure doesn\u2019t interfere with Zofran or similar anti-emetics \u2014 they work on different receptor pathways. Many women use P6 pressure to reduce how often they need medication, reserving it for breakthrough nausea rather than daily use.</p></div>';
      }

      if (triedArr.includes('small-meals')) {
        html += '<div class="callout"><div class="callout-label">On meal timing</div><p>Smaller meals help because your GLP-1 slows gastric emptying. The pre-meal P6 routine works with this \u2014 3 minutes of pressure before eating helps your stomach prepare for food rather than reacting to it. Pair the two and you\u2019re addressing nausea from both sides.</p></div>';
      }

      html += '</div>';
    }

    // ─── SECTION 5: SEVERITY-SPECIFIC NOTE ───
    if (severity === 'severe') {
      html += '<div class="protocol-section">';
      html += '<div class="callout"><div class="callout-label">A note on severity</div><p>You indicated the nausea is severe enough that you\u2019ve considered stopping your medication. P6 acupressure can help \u2014 but please also talk to your prescriber about dose adjustment. Some women find that stepping back one dose level and titrating more slowly, combined with consistent P6 pressure, lets them reach their target dose with manageable nausea. The routine above is intensive because your situation calls for it.</p></div>';
      html += '</div>';
    }

    var resultsContent = document.getElementById('results-content');
    if (resultsContent) resultsContent.innerHTML = html;
  }

  // ─── HELPER: Pattern description ───
  function getPatternDescription(pattern) {
    return ({
      morning:   'worst in the mornings before eating',
      postmeal:  'triggered or worsened by meals',
      injection: 'spiking around injection day',
      random:    'arriving in unpredictable waves',
      constant:  'persistent throughout the day'
    })[pattern] || '';
  }

  // ─── HELPER: Build daily timing protocol ───
  function buildTimingProtocol(pattern, severity, stage) {
    var html = '';
    var intense = severity === 'severe' || stage === 'starting' || stage === 'titrating';

    html += '<div class="protocol-card">';

    if (pattern === 'morning' || pattern === 'constant') {
      html += '<div class="timing-row">'
        + '<span class="timing-when">Before rising</span>'
        + '<span class="timing-what">P6 pressure on both wrists while still lying down. This is your most important window \u2014 empty stomach, rising cortisol, peak medication levels.</span>'
        + '<span class="timing-duration">' + (intense ? '3' : '2') + 'min \u00D7 2</span>'
        + '</div>';
    }

    if (pattern === 'postmeal' || pattern === 'constant' || pattern === 'random') {
      html += '<div class="timing-row">'
        + '<span class="timing-when">Pre-meal</span>'
        + '<span class="timing-what">5\u201310 minutes before eating. Your medication slows gastric emptying \u2014 pressure before food helps your stomach prepare rather than react.</span>'
        + '<span class="timing-duration">3min \u00D7 2</span>'
        + '</div>';
    }

    if (pattern === 'random' || pattern === 'constant') {
      html += '<div class="timing-row">'
        + '<span class="timing-when">Midday</span>'
        + '<span class="timing-what">A maintenance session between lunch and dinner. For unpredictable nausea, consistent pressure across the day is more effective than reactive use.</span>'
        + '<span class="timing-duration">2min \u00D7 2</span>'
        + '</div>';
    }

    if (pattern === 'morning') {
      html += '<div class="timing-row">'
        + '<span class="timing-when">Pre-meal</span>'
        + '<span class="timing-what">Before your first meal. Even if the morning nausea has passed, prepping your stomach before eating reduces the chance of a rebound wave.</span>'
        + '<span class="timing-duration">2min \u00D7 2</span>'
        + '</div>';
    }

    if (pattern === 'injection') {
      html += '<div class="timing-row">'
        + '<span class="timing-when">Morning</span>'
        + '<span class="timing-what">Standard morning session on non-injection days. Keep it consistent \u2014 even on days you feel fine. The benefit compounds with regularity.</span>'
        + '<span class="timing-duration">2min \u00D7 2</span>'
        + '</div>';
    }

    // Acute protocol for everyone
    html += '<div class="timing-row">'
      + '<span class="timing-when">Acute wave</span>'
      + '<span class="timing-what">When nausea spikes unexpectedly \u2014 firm, steady pressure immediately. Both wrists, no interruptions. Breathe slowly through your nose.</span>'
      + '<span class="timing-duration">' + (intense ? '3\u20135' : '3') + 'min \u00D7 2</span>'
      + '</div>';

    if (intense) {
      html += '<div class="timing-row">'
        + '<span class="timing-when">Before bed</span>'
        + '<span class="timing-what">An evening session helps reduce the severity of morning nausea the next day. This is especially important during your current stage.</span>'
        + '<span class="timing-duration">2min \u00D7 2</span>'
        + '</div>';
    }

    html += '</div>';
    return html;
  }

  // ─── HELPER: Build injection day protocol ───
  function buildInjectionProtocol(day, medication, severity) {
    var peakWindow = medication === 'tirzepatide' ? '12\u201336 hours' : '24\u201348 hours';
    var nextDay = getNextDay(day);
    var dayAfter = getNextDay(nextDay);
    var intense = severity === 'severe';

    var html = '<div class="protocol-card">';
    html += '<h4>' + day + ' \u2014 Injection Day</h4>';
    html += '<div class="timing-row">'
      + '<span class="timing-when">Morning</span>'
      + '<span class="timing-what">Full morning routine before your injection</span>'
      + '<span class="timing-duration">' + (intense ? '3' : '2') + 'min \u00D7 2</span>'
      + '</div>';
    html += '<div class="timing-row">'
      + '<span class="timing-when">Post-injection</span>'
      + '<span class="timing-what">30 minutes after your shot \u2014 P6 pressure on both wrists</span>'
      + '<span class="timing-duration">3min \u00D7 2</span>'
      + '</div>';
    html += '<div class="timing-row">'
      + '<span class="timing-when">Before bed</span>'
      + '<span class="timing-what">Final session to reduce overnight nausea build-up</span>'
      + '<span class="timing-duration">3min \u00D7 2</span>'
      + '</div>';
    html += '</div>';

    html += '<div class="protocol-card">';
    html += '<h4>' + nextDay + ' \u2014 Peak Window</h4>';
    html += '<p>This is typically the hardest day. Nausea peaks ' + peakWindow + ' post-injection. Apply pressure at every window in your daily routine, plus:</p>';
    html += '<div class="timing-row">'
      + '<span class="timing-when">On waking</span>'
      + '<span class="timing-what">Extended morning session \u2014 stay in bed a few extra minutes with pressure applied</span>'
      + '<span class="timing-duration">' + (intense ? '5' : '3') + 'min \u00D7 2</span>'
      + '</div>';
    html += '<div class="timing-row">'
      + '<span class="timing-when">Midday</span>'
      + '<span class="timing-what">Additional session even if you don\u2019t feel nauseous \u2014 preventive, not reactive</span>'
      + '<span class="timing-duration">3min \u00D7 2</span>'
      + '</div>';
    html += '</div>';

    html += '<div class="protocol-card">';
    html += '<h4>' + dayAfter + ' \u2014 Recovery</h4>';
    html += '<p>Nausea typically starts easing. Return to your standard daily routine. If you\u2019re still experiencing significant nausea by ' + dayAfter + ' evening, next week consider applying pressure more frequently in the first 12 hours post-injection.</p>';
    html += '</div>';

    return html;
  }

  // ─── HELPER: Get next day of the week ───
  function getNextDay(day) {
    var days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    var idx = days.indexOf(day);
    return days[(idx + 1) % 7];
  }

  // ─── SCROLL-TRIGGERED REVEALS ───
  function initScrollReveals() {
    var fadeObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.sl-quiz .fade-in').forEach(function(el) {
      fadeObserver.observe(el);
    });
  }

  // ─── INIT ───
  function init() {
    initOptionSelection();
    initScrollReveals();

    // Show the first screen
    var firstScreen = document.getElementById('screen-0');
    if (firstScreen) firstScreen.classList.add('visible');
  }

  // ─── EXPOSE API — used by onclick="slQuiz.goTo(n)" in the Liquid template ───
  window.slQuiz = {
    goTo: goTo,
    submitEmail: submitEmail
  };

  // Initialise when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
